import { ensureDefined } from "common/utils/assertOfType";

/**
 * Functions to join PolyLine's together
 * @module
 */
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { line2IsParallel, line2Tangent } from "./line2";
import {
  polyLine2Concat,
  polyLine2ConcatAll,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2IsClosed,
  polyLine2MergeCoincidentVertices,
  polyLine2MergeCollinearEdges,
  polyLine2Reverse,
  polyLine2ReverseCopy,
  polyLine2Simplify,
  polyLine2Start,
} from "./polyLine2";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import {
  polyLine3Concat,
  polyLine3Copy,
  polyLine3EndPoint,
  polyLine3IsClosed,
  polyLine3Reverse,
  polyLine3Simplify,
  polyLine3StartPoint,
} from "./polyLine3";
import { vec2DistSquared, vec2Dot } from "./vec2";
import { vec3DistSquared } from "./vec3";

export function joinPolyLine2Sequences(
  pls: PolyLine2[],
  epsilonSq: number = DIST_EPSILON_SQUARED,
  simplify = true,
): PolyLine2[] {
  const sequences: PolyLine2[] = [];
  let currSequence: PolyLine2 | null = null;
  const finalizeSequence = () => {
    if (currSequence) {
      currSequence = simplify ? polyLine2Simplify(currSequence) : polyLine2MergeCoincidentVertices(currSequence);
      sequences.push(currSequence);
    }
    currSequence = null;
  };

  const addPolyLine = (segment: PolyLine2) => {
    if (currSequence) {
      const [endX, endY] = polyLine2EdgeEnd(currSequence, polyLine2EdgeCount(currSequence) - 1);
      const [startX, startY] = polyLine2EdgeStart(segment, 0);
      if (vec2DistSquared(endX, endY, startX, startY) < epsilonSq) {
        currSequence = polyLine2Concat(currSequence, segment);
      } else {
        finalizeSequence();
      }
    }

    if (!currSequence) {
      currSequence = polyLine2Copy(segment);
    }
  };

  for (let i = 0, l = pls.length; i < l; i++) {
    addPolyLine(pls[i]);
  }

  finalizeSequence();

  // try to merge last and first
  if (sequences.length > 1) {
    const lastSequence = sequences[sequences.length - 1];
    const firstSequence = sequences[0];

    const [endX, endY] = polyLine2EdgeEnd(lastSequence, polyLine2EdgeCount(lastSequence) - 1);
    const [startX, startY] = polyLine2EdgeStart(firstSequence, 0);

    if (vec2DistSquared(endX, endY, startX, startY) < epsilonSq) {
      sequences[0] = simplify
        ? polyLine2Simplify(polyLine2Concat(lastSequence, firstSequence))
        : polyLine2MergeCoincidentVertices(polyLine2Concat(lastSequence, firstSequence));
      sequences.pop();
    }
  }

  return sequences.filter((x) => x.length >= 4);
}

/**
 * This algorithm joins PolyLine2s by looking at the ends of the other PolyLine2s in the input
 * and joining them if they are within a tolerance. It doesn't mutate the input. It may reverse
 * some of the input to join them.
 *
 * If the length of the input is <2, it returns the input (copying each one).
 *
 * This function is O(n^2). Hence, it assumes small input sizes.
 *
 * @param pls Unstructured PolyLine2s. The sy
 * @param epsilonSq tolerance to connect PolyLine2s
 */
export function joinPolyLine2s(pls: PolyLine2[], epsilonSq: number = DIST_EPSILON_SQUARED): PolyLine2[] {
  if (pls.length < 2) {
    return pls.map(polyLine2Copy);
  }

  // make a copy as we will mutate the input array
  pls = pls.slice();

  /**
   * This algorithm is very simple. It pops a PolyLine2 and tries to find one to connect it to,
   * storing the current PolyLine2 in a variable called current.
   *
   * - If it can find one, it joins them and continues (4 cases)
   * - If it can't find one, it ends the current PolyLine2 and starts a new one.
   * - If there are none left, the loop terminates
   */

  const joined: PolyLine2[] = [];

  const first = ensureDefined(pls.shift());

  let current: PolyLine2 | undefined = polyLine2Copy(first);

  while (current) {
    let found = -1;

    const [startX, startY] = polyLine2Start(current);
    const [endX, endY] = polyLine2End(current);

    for (let i = 0; i < pls.length; i++) {
      const candidate = pls[i];

      const [candidateStartX, candidateStartY] = polyLine2Start(candidate);
      const [candidateEndX, candidateEndY] = polyLine2End(candidate);

      if (vec2DistSquared(endX, endY, candidateStartX, candidateStartY) < epsilonSq) {
        //  CURR    CAND
        // ----->  ----->
        current = polyLine2Concat(current, candidate.slice(2));
        found = i;
        break;
      } else if (vec2DistSquared(startX, startY, candidateEndX, candidateEndY) < epsilonSq) {
        //  CURR    CAND
        // <-----  <-----
        current = polyLine2Concat(candidate, current.slice(2));
        found = i;
        break;
      } else if (vec2DistSquared(endX, endY, candidateEndX, candidateEndY) < epsilonSq) {
        //  CURR    CAND
        // ----->  <-----
        current = polyLine2Concat(current, polyLine2ReverseCopy(candidate).slice(2));
        found = i;
        break;
      } else if (vec2DistSquared(startX, startY, candidateStartX, candidateStartY) < epsilonSq) {
        //  CAND    CURR
        // <-----  ----->
        current = polyLine2Concat(polyLine2ReverseCopy(candidate), current.slice(2));
        found = i;
        break;
      }
    }

    if (found === -1) {
      // we found no neighbors to connect to
      // finish current by pushing it to joined list
      // and get a new one from the input list
      joined.push(current);
      current = pls.shift();
    } else {
      // remove the found pl that was joined with current
      pls.splice(found, 1);
    }
  }

  return joined;
}

// joinPolyLineCollinearSequences joins polyline2 collinear strips
export function joinPolyLineCollinearSequences(
  pls: PolyLine2[],
  epsilonSq: number = DIST_EPSILON_SQUARED,
): PolyLine2[] {
  const sequences: PolyLine2[] = [];
  let currSequence: PolyLine2 | null = null;
  const finalizeSequence = () => {
    if (currSequence) {
      currSequence = polyLine2Simplify(currSequence);
      sequences.push(currSequence);
    }
    currSequence = null;
  };

  const addPolyLine = (segment: PolyLine2) => {
    if (currSequence) {
      const [endX, endY] = polyLine2EdgeEnd(currSequence, polyLine2EdgeCount(currSequence) - 1);
      const [startX, startY] = polyLine2EdgeStart(segment, 0);

      if (vec2DistSquared(endX, endY, startX, startY) < epsilonSq) {
        const [p1x, p1y] = polyLine2EdgeTangent(currSequence, polyLine2EdgeCount(currSequence) - 1);
        const [p2x, p2y] = polyLine2EdgeTangent(segment, 0);

        const dot = Math.abs(vec2Dot(p1x, p1y, p2x, p2y));

        if (Math.abs(1 - dot) <= ANGLE_EPSILON) {
          currSequence = polyLine2Concat(currSequence, segment);
        } else {
          finalizeSequence();
        }
      } else {
        finalizeSequence();
      }
    }

    if (!currSequence) {
      currSequence = polyLine2Copy(segment);
    }
  };

  for (let i = 0, l = pls.length; i < l; i++) {
    addPolyLine(pls[i]);
  }

  finalizeSequence();

  // try to merge last and first
  if (sequences.length > 1) {
    const lastSequence = sequences[sequences.length - 1];
    const firstSequence = sequences[0];

    const [endX, endY] = polyLine2EdgeEnd(lastSequence, polyLine2EdgeCount(lastSequence) - 1);
    const [startX, startY] = polyLine2EdgeStart(firstSequence, 0);

    if (vec2DistSquared(endX, endY, startX, startY) < epsilonSq) {
      const [p1x, p1y] = polyLine2EdgeTangent(lastSequence, 0);
      const [p2x, p2y] = polyLine2EdgeTangent(firstSequence, 0);

      const dot = Math.abs(vec2Dot(p1x, p1y, p2x, p2y));

      if (Math.abs(1 - dot) <= ANGLE_EPSILON) {
        sequences[0] = polyLine2Simplify(polyLine2Concat(lastSequence, firstSequence));
        sequences.pop();
      }
    }
  }

  return sequences.filter((x) => x.length >= 4);
}

// joinUnorderedPolyLine2Sequence is not as robust as joinPolyLine2s. It can only
// join a sequence of PolyLine2s into one single PolyLine2 without closing.

export function joinPolyLine2Sequence(pls: PolyLine2[]): PolyLine2 {
  const outpl = polyLine2New(pls.reduce((vertCount, pl) => (vertCount += pl.length - 2), 2));

  let cursor = 2;

  outpl[0] = pls[0][0];
  outpl[1] = pls[0][1];

  for (let i = 0, l = pls.length; i < l; i++) {
    const pl = pls[i];
    outpl.set(pl.slice(2), cursor);
    cursor += pl.length - 2;
  }

  return outpl;
}

// joinPolyLine2sIntoOne is not as robust as joinPolyLine2s. It can only
// join a sequence of PolyLine2s into one single PolyLine2 without closing.

export function joinPolyLine2sIntoOne(pls: PolyLine2[], mergeCollinearEdges: boolean): PolyLine2 | null {
  if (pls.length === 0) {
    return null;
  }
  if (pls.length === 1) {
    return pls[0];
  }

  let a = pls[0];

  const joined = new Array<boolean>(pls.length);
  joined.fill(false);

  for (let i = 1, l = pls.length; i < l; i++) {
    for (let j = 1, l = pls.length; j < l; j++) {
      if (joined[j]) {
        continue;
      }

      const b = pls[j];
      const al = a.length;
      const bl = b.length;

      if (vec2DistSquared(a[0], a[1], b[0], b[1]) < DIST_EPSILON_SQUARED) {
        const br = polyLine2Reverse(polyLine2Copy(b));
        a = polyLine2Concat(br.slice(0, bl - 2), a);
        joined[j] = true;
      } else if (vec2DistSquared(a[0], a[1], b[bl - 2], b[bl - 1]) < DIST_EPSILON_SQUARED) {
        a = polyLine2Concat(b.slice(0, bl - 2), a);
        joined[j] = true;
      } else if (vec2DistSquared(a[al - 2], a[al - 1], b[0], b[1]) < DIST_EPSILON_SQUARED) {
        a = polyLine2Concat(a.slice(0, al - 2), b);
        joined[j] = true;
      } else if (vec2DistSquared(a[al - 2], a[al - 1], b[bl - 2], b[bl - 1]) < DIST_EPSILON_SQUARED) {
        const br = polyLine2Reverse(polyLine2Copy(b));
        a = polyLine2Concat(a.slice(0, al - 2), br);
        joined[j] = true;
      }
    }
  }

  if (mergeCollinearEdges) {
    return polyLine2MergeCollinearEdges(a);
  }

  return a;
}

export function joinPolyLine2Loops(segments: PolyLine2[], tolerance: number = DIST_EPSILON) {
  segments = segments.slice(0);

  // put all of the segment ends (expressed in 2d coordinate space in the cut plane) into an rtree
  // then iterate through them, looking for the ends and closing them

  interface RBox {
    x: number;
    y: number;
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    segmentIndex: number;
    isStart: boolean;
  }

  const segmentVisited = new Array<boolean>(segments.length).fill(false);
  const finalLoops: PolyLine2[] = [];

  const rbushItems: RBox[] = [];
  for (let i = 0, l = segments.length; i < l; i++) {
    const seg = segments[i];

    if (polyLine2IsClosed(seg)) {
      finalLoops.push(seg);
      segmentVisited[i] = true;
      continue;
    }

    const ax = seg[0];
    const ay = seg[1];

    const bx = seg[seg.length - 2];
    const by = seg[seg.length - 1];

    rbushItems.push({
      minX: ax - DIST_EPSILON,
      minY: ay - DIST_EPSILON,
      maxX: ax + DIST_EPSILON,
      maxY: ay + DIST_EPSILON,
      x: ax,
      y: ay,
      segmentIndex: i,
      isStart: true,
    });

    rbushItems.push({
      minX: bx - DIST_EPSILON,
      minY: by - DIST_EPSILON,
      maxX: bx + DIST_EPSILON,
      maxY: by + DIST_EPSILON,
      x: bx,
      y: by,
      segmentIndex: i,
      isStart: false,
    });
  }

  const rtree = new RBush<RBox>();
  rtree.load(rbushItems);

  // build the loops

  const unjoinedLoops: PolyLine2[][] = []; // loops are stored here

  // reuse the search box
  const searchBox = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  for (let i = 0, l = segments.length; i < l; i++) {
    if (segmentVisited[i]) {
      continue;
    }

    const loop: PolyLine2[] = [];
    unjoinedLoops.push(loop);

    let currentSegIndex = i;

    while (true) {
      const currentSeg = segments[currentSegIndex];
      loop.push(currentSeg);

      segmentVisited[currentSegIndex] = true;

      const x = currentSeg[currentSeg.length - 2];
      const y = currentSeg[currentSeg.length - 1];

      // make box around end point
      searchBox.minX = x - tolerance;
      searchBox.minY = y - tolerance;

      searchBox.maxX = x + tolerance;
      searchBox.maxY = y + tolerance;

      const res = rtree.search(searchBox);

      if (res.length === 2) {
        const segIndex0 = res[0].segmentIndex;
        const segIndex1 = res[1].segmentIndex;
        let isStart = false;

        // cases:
        if (segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
          // both pts have been visited - close loop
          break;
        } else if (!segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
          // segment end 0 is visited - extend loop with that segment
          currentSegIndex = segIndex0;
          isStart = res[0].isStart;
        } else if (segmentVisited[segIndex0] && !segmentVisited[segIndex1]) {
          // segment end 1 is visited - extend loop with that segment
          currentSegIndex = segIndex1;
          isStart = res[1].isStart;
        } else {
          // illegal case
          console.assert(false, "I could not close one of the loops.");
          return [];
        }

        const newSeg = segments[currentSegIndex];

        if (!isStart) {
          segments[currentSegIndex] = polyLine2ReverseCopy(newSeg);
        }
      } else {
        console.assert(
          false,
          `I could not close one of the intersection loops. There are ${res.length} intersection segments intersecting at (${x}, ${y}), but there should be 2.`,
        );
        return [];
      }
    }
  }

  for (let i = 0, li = unjoinedLoops.length; i < li; i++) {
    const loopSet = unjoinedLoops[i];
    finalLoops.push(polyLine2ConcatAll(loopSet, true));
  }

  return finalLoops;
}

export function joinExteriorWallDimensionSegments(
  pls: PolyLine2[],
  mergeCollinearSegments: boolean = true,
): PolyLine2 | null {
  if (pls.length === 0) {
    return null;
  }
  if (pls.length === 1) {
    return pls[0];
  }

  let a = pls[0];

  const joined = new Array<boolean>(pls.length);
  joined.fill(false);

  for (let i = 1, l = pls.length; i < l; i++) {
    for (let j = 1; j < l; j++) {
      if (joined[j]) {
        continue;
      }

      const b = pls[j];
      const al = a.length;
      const bl = b.length;

      if (vec2DistSquared(a[0], a[1], b[bl - 2], b[bl - 1]) < DIST_EPSILON_SQUARED) {
        const [t1X, t1Y] = line2Tangent(a[0], a[1], a[2], a[3]);
        const [t2X, t2Y] = line2Tangent(b[bl - 4], b[bl - 3], b[bl - 2], b[bl - 1]);
        const dot = vec2Dot(t1X, t1Y, t2X, t2Y);

        if (mergeCollinearSegments && dot > 1.0 - DIST_EPSILON && dot < 1.0 + DIST_EPSILON) {
          a = polyLine2Concat(b.slice(0, bl - 2), a.slice(2, al));
        } else {
          a = polyLine2Concat(b.slice(0, bl - 2), a);
        }

        joined[j] = true;
      } else if (vec2DistSquared(a[al - 2], a[al - 1], b[0], b[1]) < DIST_EPSILON_SQUARED) {
        const [t1X, t1Y] = line2Tangent(a[al - 4], a[al - 3], a[al - 2], a[al - 1]);
        const [t2X, t2Y] = line2Tangent(b[0], b[1], b[2], b[3]);
        const dot = vec2Dot(t1X, t1Y, t2X, t2Y);

        if (mergeCollinearSegments && dot > 1.0 - DIST_EPSILON && dot < 1.0 + DIST_EPSILON) {
          a = polyLine2Concat(a.slice(0, al - 2), b.slice(2, bl));
        } else {
          a = polyLine2Concat(a.slice(0, al - 2), b);
        }

        joined[j] = true;
      }
    }
  }

  if (mergeCollinearSegments) {
    const al = a.length;
    if (vec2DistSquared(a[0], a[1], a[al - 2], a[al - 1]) < DIST_EPSILON_SQUARED) {
      if (line2IsParallel(a[al - 4], a[al - 3], a[0], a[1], a[0], a[1], a[2], a[3])) {
        a[al - 2] = a[2];
        a[al - 1] = a[3];
        a = a.slice(2);
      }
    }
  }

  return a;
}

interface JoinPolyLine3RailsOptions {
  segments: PolyLine3[];
  searchBoxTolerance?: number;
  angleTolerance?: number;
  simplify?: boolean;
  allowRevert?: boolean;
  toleranceSq?: number;
}

export function joinPolyLine3Rails({
  segments,
  searchBoxTolerance = DIST_EPSILON,
  angleTolerance = ANGLE_EPSILON,
  simplify = false,
  allowRevert = true,
  toleranceSq = searchBoxTolerance * searchBoxTolerance,
}: JoinPolyLine3RailsOptions): PolyLine3[] {
  interface RCube {
    x: number;
    y: number;
    z: number;
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
    segmentIndex: number;
    isStart: boolean;
  }

  const segmentVisited = new Array<boolean>(segments.length).fill(false);
  const finalRails: PolyLine3[] = [];

  const rbushItems: RCube[] = [];
  for (let i = 0, l = segments.length; i < l; i++) {
    const seg = segments[i];

    if (polyLine3IsClosed(seg)) {
      finalRails.push(seg);
      segmentVisited[i] = true;
      continue;
    }

    const ax = seg[0];
    const ay = seg[1];
    const az = seg[2];

    const bx = seg[seg.length - 3];
    const by = seg[seg.length - 2];
    const bz = seg[seg.length - 1];

    rbushItems.push({
      minX: ax - DIST_EPSILON,
      minY: ay - DIST_EPSILON,
      minZ: az - DIST_EPSILON,
      maxX: ax + DIST_EPSILON,
      maxY: ay + DIST_EPSILON,
      maxZ: az + DIST_EPSILON,
      x: ax,
      y: ay,
      z: az,
      segmentIndex: i,
      isStart: true,
    });

    rbushItems.push({
      minX: bx - DIST_EPSILON,
      minY: by - DIST_EPSILON,
      minZ: bz - DIST_EPSILON,
      maxX: bx + DIST_EPSILON,
      maxY: by + DIST_EPSILON,
      maxZ: bz + DIST_EPSILON,
      x: bx,
      y: by,
      z: bz,
      segmentIndex: i,
      isStart: false,
    });
  }

  const rtree = new RBush3D();
  rtree.load(rbushItems);

  // reuse the search box
  const searchBox = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };

  const checkEnd = (currentRail: PolyLine3, currentSegIndex: number, isStart: boolean): PolyLine3 => {
    const vertexId = isStart ? 0 : currentRail.length - 3;

    const x = currentRail[vertexId];
    const y = currentRail[vertexId + 1];
    const z = currentRail[vertexId + 2];

    // make box around end point
    searchBox.minX = x - searchBoxTolerance;
    searchBox.minY = y - searchBoxTolerance;
    searchBox.minZ = z - searchBoxTolerance;

    searchBox.maxX = x + searchBoxTolerance;
    searchBox.maxY = y + searchBoxTolerance;
    searchBox.maxZ = z + searchBoxTolerance;

    const res = rtree.search(searchBox);

    if (res.length >= 2) {
      // look for the best point
      let firstValidResult = -1;
      for (let j = 0, lj = res.length; j < lj; j++) {
        if (res[j].segmentIndex === currentSegIndex) {
          firstValidResult = j;
          break;
        }
      }

      if (firstValidResult < 0) {
        return currentRail;
      }

      let nextValidResult = -1;
      let bestDist = Number.MAX_VALUE;
      for (let j = 1, lj = res.length; j < lj; j++) {
        const nextJ = (firstValidResult + j) % lj;
        const nextRes = res[nextJ];

        if (segmentVisited[nextRes.segmentIndex]) {
          continue;
        }

        const nextSegment = segments[nextRes.segmentIndex];
        const [nextX, nextY, nextZ] = nextRes.isStart
          ? polyLine3StartPoint(nextSegment)
          : polyLine3EndPoint(nextSegment);
        const dist = vec3DistSquared(x, y, z, nextX, nextY, nextZ);

        if (dist < bestDist) {
          bestDist = dist;
          nextValidResult = nextJ;
        }
      }

      if (nextValidResult < 0) {
        return currentRail;
      }

      const segIndex0 = res[firstValidResult].segmentIndex;
      const segIndex1 = res[nextValidResult].segmentIndex;
      let isNextStart = false;
      let nextSegmentIndex = currentSegIndex;
      // cases:
      if (segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
        // both pts have been visited - finish rail
        return currentRail;
      } else if (!segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
        // segment end 0 is visited - extend rail with that segment
        nextSegmentIndex = segIndex0;
        isNextStart = res[firstValidResult].isStart;
      } else if (segmentVisited[segIndex0] && !segmentVisited[segIndex1]) {
        // segment end 1 is visited - extend rail with that segment
        nextSegmentIndex = segIndex1;
        isNextStart = res[nextValidResult].isStart;
      } else {
        // illegal case
        console.assert(false, "Error joining rails.");
        return currentRail;
      }

      let nextRail = polyLine3Copy(segments[segIndex1]);

      if (isStart === isNextStart) {
        if (allowRevert) {
          nextRail = polyLine3Reverse(nextRail);
        } else {
          return currentRail;
        }
      }

      if (isStart) {
        currentRail = polyLine3Concat(nextRail, currentRail);
      } else {
        currentRail = polyLine3Concat(currentRail, nextRail);
      }

      segmentVisited[nextSegmentIndex] = true;

      return checkEnd(currentRail, nextSegmentIndex, isStart);
    } else {
      return currentRail;
    }
  };

  for (let i = 0, l = segments.length; i < l; i++) {
    if (segmentVisited[i]) {
      continue;
    }

    const currentSeg = segments[i];
    let currentRail = polyLine3Copy(currentSeg);

    segmentVisited[i] = true;

    currentRail = checkEnd(currentRail, i, true);
    currentRail = checkEnd(currentRail, i, false);

    if (simplify) {
      currentRail = polyLine3Simplify(currentRail, toleranceSq, angleTolerance);
    }

    finalRails.push(currentRail);
  }

  return finalRails;
}

interface JoinPolyLine2RailsOptions {
  segments: PolyLine2[];
  tolerance?: number;
  simplify?: boolean;
  allowRevert?: boolean;
}

export function joinPolyLine2Rails({
  segments,
  tolerance = DIST_EPSILON,
  simplify = false,
  allowRevert = true,
}: JoinPolyLine2RailsOptions): PolyLine2[] {
  interface RBox {
    x: number;
    y: number;
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    segmentIndex: number;
    isStart: boolean;
  }

  const segmentVisited = new Array<boolean>(segments.length).fill(false);
  const finalRails: PolyLine2[] = [];

  const rbushItems: RBox[] = [];
  for (let i = 0, l = segments.length; i < l; i++) {
    const seg = segments[i];

    if (polyLine2IsClosed(seg)) {
      finalRails.push(seg);
      segmentVisited[i] = true;
      continue;
    }

    const ax = seg[0];
    const ay = seg[1];

    const bx = seg[seg.length - 2];
    const by = seg[seg.length - 1];

    rbushItems.push({
      minX: ax - DIST_EPSILON,
      minY: ay - DIST_EPSILON,
      maxX: ax + DIST_EPSILON,
      maxY: ay + DIST_EPSILON,
      x: ax,
      y: ay,
      segmentIndex: i,
      isStart: true,
    });

    rbushItems.push({
      minX: bx - DIST_EPSILON,
      minY: by - DIST_EPSILON,
      maxX: bx + DIST_EPSILON,
      maxY: by + DIST_EPSILON,
      x: bx,
      y: by,
      segmentIndex: i,
      isStart: false,
    });
  }

  const rtree = new RBush<RBox>();
  rtree.load(rbushItems);

  // reuse the search box
  const searchBox = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 };

  const checkEnd = (currentRail: PolyLine2, currentSegIndex: number, isStart: boolean): PolyLine2 => {
    const vertexId = isStart ? 0 : currentRail.length - 2;

    const x = currentRail[vertexId];
    const y = currentRail[vertexId + 1];

    // make box around end point
    searchBox.minX = x - tolerance;
    searchBox.minY = y - tolerance;

    searchBox.maxX = x + tolerance;
    searchBox.maxY = y + tolerance;

    const res = rtree.search(searchBox);

    if (res.length >= 2) {
      // look for the best point
      let firstValidResult = -1;
      for (let j = 0, lj = res.length; j < lj; j++) {
        if (res[j].segmentIndex === currentSegIndex) {
          firstValidResult = j;
          break;
        }
      }

      if (firstValidResult < 0) {
        return currentRail;
      }

      let nextValidResult = -1;
      let bestDist = Number.MAX_VALUE;
      for (let j = 1, lj = res.length; j < lj; j++) {
        const nextJ = (firstValidResult + j) % lj;
        const nextRes = res[nextJ];

        if (segmentVisited[nextRes.segmentIndex]) {
          continue;
        }

        const nextSegment = segments[nextRes.segmentIndex];
        const [nextX, nextY] = nextRes.isStart ? polyLine2Start(nextSegment) : polyLine2End(nextSegment);
        const dist = vec2DistSquared(x, y, nextX, nextY);

        if (dist < bestDist) {
          bestDist = dist;
          nextValidResult = nextJ;
        }
      }

      if (nextValidResult < 0) {
        return currentRail;
      }

      const segIndex0 = res[firstValidResult].segmentIndex;
      const segIndex1 = res[nextValidResult].segmentIndex;
      let isNextStart = false;
      let nextSegmentIndex = currentSegIndex;
      // cases:
      if (segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
        // both pts have been visited - finish rail
        return currentRail;
      } else if (!segmentVisited[segIndex0] && segmentVisited[segIndex1]) {
        // segment end 0 is visited - extend rail with that segment
        nextSegmentIndex = segIndex0;
        isNextStart = res[firstValidResult].isStart;
      } else if (segmentVisited[segIndex0] && !segmentVisited[segIndex1]) {
        // segment end 1 is visited - extend rail with that segment
        nextSegmentIndex = segIndex1;
        isNextStart = res[nextValidResult].isStart;
      } else {
        // illegal case
        console.assert(false, "Error joining rails.");
        return currentRail;
      }

      let nextRail = polyLine2Copy(segments[segIndex1]);

      if (isStart === isNextStart) {
        if (allowRevert) {
          nextRail = polyLine2Reverse(nextRail);
        } else {
          return currentRail;
        }
      }

      if (isStart) {
        currentRail = polyLine2Concat(nextRail, currentRail);
      } else {
        currentRail = polyLine2Concat(currentRail, nextRail);
      }

      segmentVisited[nextSegmentIndex] = true;

      return checkEnd(currentRail, nextSegmentIndex, isStart);
    } else {
      return currentRail;
    }
  };

  for (let i = 0, l = segments.length; i < l; i++) {
    if (segmentVisited[i]) {
      continue;
    }

    const currentSeg = segments[i];
    let currentRail = polyLine2Copy(currentSeg);

    segmentVisited[i] = true;

    currentRail = checkEnd(currentRail, i, true);
    currentRail = checkEnd(currentRail, i, false);

    if (simplify) {
      currentRail = polyLine2Simplify(currentRail, tolerance);
    }

    finalRails.push(currentRail);
  }

  return finalRails;
}
